import React from 'react';
import { Row, Col, Input, Card, Tag, Tooltip, Button, Spin } from 'antd';
import { truncateString } from '@/utils/utils';
import { FormattedMessage, formatMessage } from 'umi-plugin-react/locale';
import { ThunderboltOutlined } from '@ant-design/icons';
import styles from '../index.less';
import { capitalizeFirstLetter } from '../../../../helpers/capitalizeFirstLetter';
import ProgressRisk from '@/components/WidgetWrapper/widgets/components/ProgressRisk';
import AllServices from '../../../../../config/AllServices';
import SingleResourceData from './SingleResourceData';
import RiskProgressBar from './RiskProgressBar';
import HealthChart from './HealthChart';
import SingleResourcePropertiesTable from '../SingleResourcePropertiesTable';
import SingleResourcePropertiesData from './SingleResourcePropertiesData';
import ProviderLogo from '@/components/ProviderLogo/ProviderLogo';

const { Meta } = Card;

const { Search } = Input;

function SingleResourceMetaData(props) {
  let {
    region,
    service,
    cloudAccName,
    vpc,
    Status,
    riskLevel,
    failed,
    passed,
    malwareFailed,
    osVulnerabilitiesFailed,
    noOfRisk,
    healthLoading,
    resource,
    resourceProperties,
    provider,
  } = props;
  region = region && !Array.isArray(region) ? region : 'Global';

  const progressObj = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    passed: 0,
    total: 0,
  };

  if (failed && failed.length > 0) {
    failed.forEach(val => {
      const risk = val.risk.toLowerCase();
      if (risk === 'critical') progressObj.critical++;
      else if (risk === 'high') progressObj.high++;
      else if (risk === 'medium') progressObj.medium++;
      else if (risk === 'low' || risk === 'n/a') progressObj.low++;
    });
  }
  if (malwareFailed && malwareFailed.length > 0) {
    malwareFailed.forEach(val => {
      const severity = val.severity.toLowerCase();
      if (severity === 'critical') progressObj.critical++;
      else if (severity === 'high') progressObj.high++;
      else if (severity === 'medium') progressObj.medium++;
      else if (severity === 'low' || severity === 'n/a') progressObj.low++;
    });
  }
  if (osVulnerabilitiesFailed && osVulnerabilitiesFailed.length > 0) {
    osVulnerabilitiesFailed.forEach(val => {
      const cvss3Severity = val.cvss3Severity?.toLowerCase();
      if (cvss3Severity === 'critical') progressObj.critical++;
      else if (cvss3Severity === 'high') progressObj.high++;
      else if (cvss3Severity === 'medium') progressObj.medium++;
      else if (cvss3Severity === 'low' || cvss3Severity === 'n/a') progressObj.low++;
    });
  }

  const passedRiskCount = passed;
  const total =
    progressObj.critical +
    progressObj.high +
    progressObj.medium +
    progressObj.low +
    passedRiskCount;
  progressObj.passed = passedRiskCount;
  progressObj.total = total;

  // * create a function to get the provider name and cloud account name from the cloudAccName array
  // * loop through each items and check if the provider key is present in the item if yes then return the provider name and cloud account name
  const getAccountDetail = () => {
    let providerName = '';
    let cloudAccountName = '';
    let isAWSAccount = false;
    let isAzureAccount = false;
    let isGCPAccount = false;

    if (cloudAccName && cloudAccName.length > 0) {
      cloudAccName.forEach(item => {
        if (item.provider) {
          providerName = item.provider;
          if (item.provider.toLowerCase() === 'aws') isAWSAccount = true;
          else if (item.provider.toLowerCase() === 'azure') isAzureAccount = true;
          else if (item.provider.toLowerCase() === 'gcp') isGCPAccount = true;
        }
        if (item.cloudAccountName) {
          cloudAccountName = item.cloudAccountName;
        }
      });
    }
    return {
      providerName,
      cloudAccountName,
      isAWSAccount,
      isAzureAccount,
      isGCPAccount,
    };
  };

  return (
    <div className="sig-card" style={{ marginTop: '5px' }}>
      <Row type="flex" style={{ margin: '0 2rem 1rem 2rem' }}>
        <Col span={12}>
          <Row type="flex" style={{ marginBottom: '2rem' }} gutter={12}>
            <Col span={14}>
              <Meta
                title={<div className={styles.serviceTitle}>Cloud Account</div>}
                description={
                  <Tag>
                    <div
                      style={{
                        fontSize: '14px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '2rem',
                        maxWidth: '100%',
                        padding: '0.5rem 0.5rem',
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '100%',
                        }}
                      >
                        <ProviderLogo
                          provider={getAccountDetail()?.providerName}
                          withName={true}
                          cloudAccountName={getAccountDetail()?.cloudAccountName}
                        />
                      </div>
                    </div>
                  </Tag>
                }
              />
            </Col>
            <Col span={10}>
              <Meta
                title={<div className={styles.serviceTitle}>Region</div>}
                description={
                  <Tag color="blue">
                    <div
                      style={{
                        fontSize: '14px',
                        height: '2rem',
                        padding: '0 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {region.toLowerCase()}
                    </div>
                  </Tag>
                }
              />
            </Col>
          </Row>

          <Row type="flex" style={{ marginBottom: '2rem' }}>
            <Col span={24}>
              <Meta
                title={<div className={styles.serviceTitle}>Service</div>}
                description={
                  <Tooltip title={AllServices.getFullName(service)}>
                    <Tag color="blue">
                      <div
                        style={{
                          fontSize: '14px',
                          height: '2rem',
                          padding: '0 10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {truncateString(AllServices.getFullName(service), 40)}
                      </div>
                    </Tag>
                  </Tooltip>
                }
              />
            </Col>
          </Row>
        </Col>

        {/* DYNAMIC PROPERTIES FOR RESOURCES */}
        {resourceProperties && (
          <Col span={12}>
            <SingleResourcePropertiesData
              resourceProperties={resourceProperties}
              provider={provider}
              service={service}
            />
          </Col>
        )}
      </Row>

      <div>
        <Row style={{ marginTop: '10px', marginBottom: '10px' }}>
          <Col span={19} offset={1}>
            <Meta title={<div className={styles.rTitle}>Health Trend</div>} />
          </Col>

          <Col span={3} offset={1}>
            <div>
              <Meta title={<div className={styles.rTitle}>Risks</div>} />
            </div>
          </Col>
        </Row>
        <Row>
          <Col span={20}>
            <div
              className="mt-3"
              style={{
                marginLeft: '10px',
              }}
            >
              {healthLoading && (
                <Spin
                  size="medium"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              )}

              {!healthLoading && noOfRisk.length > 0 && (
                <HealthChart noOfRisk={noOfRisk} risks={progressObj} />
              )}

              {!healthLoading && noOfRisk.length === 0 && (
                <div
                  style={{
                    position: 'relative',
                  }}
                >
                  <HealthChart noOfRisk={noOfRisk} risks={progressObj} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '170px',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#a9a9a9',
                      position: 'absolute',
                      backdropFilter: 'blur(2px)',
                      bottom: 0,
                      width: '100%',
                    }}
                  >
                    Not enough data yet. Please check later.
                  </div>
                </div>
              )}
            </div>
          </Col>

          <Col span={3} offset={1}>
            <div className="mt-3">
              <ProgressRisk type="risks" risks={progressObj} />
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={2} style={{ marginLeft: '1.5rem' }}>
            <p style={{ fontWeight: '600', display: 'inline-block' }} className={styles.rTitle}>
              Risks
            </p>
          </Col>
          <Col span={20}>
            <RiskProgressBar />
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default SingleResourceMetaData;
